import React, { useState, useEffect, useContext } from 'react'
import API from '../../../services/API'
import Alert from '@mui/material/Alert'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { GlobalContext } from '../../../App'
import { Button } from '@mui/material'
import { Delete } from '@mui/icons-material'
import SmallModal from '../../../components/Modals/SmallModal'

const AddNewContact = ({
  existingContact,
  client,
  setOpenAddNew,
  customCallbackAction,
  loadEvent,
}) => {
  const [loadingPendingAccounts, setLoadingPendingAccounts] = useState(true)
  const [pendingAccounts, setPendingAccounts] = useState([])
  const [newManualContact, setNewManualContact] = useState({
    firstName:
      existingContact?.noAccount?.firstName ||
      existingContact?.userAccount?.firstName ||
      '',
    lastName:
      existingContact?.noAccount?.lastName ||
      existingContact?.userAccount?.lastName ||
      '',
    role: existingContact?.companyRole || '',
    email:
      existingContact?.noAccount?.email ||
      existingContact?.userAccount?.email ||
      '',
    phone: existingContact?.phone || '',
  })
  const [notes, setNotes] = useState(existingContact?.notes ?? [])
  const { setToast } = useContext(GlobalContext)
  const hasUserAccount = !!existingContact?.userAccount
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false)

  useEffect(() => {
    API.get('/pending-contact-accounts')
      .then((res) => {
        if (res.status === 200) {
          setPendingAccounts(res.data)
        }
      })
      .finally(() => setLoadingPendingAccounts(false))
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (existingContact?._id) {
      API.put(
        `/clients/edit-contact/${existingContact._id}`,
        newManualContact,
      ).then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Contact Updated' })
          setOpenAddNew(false)
          customCallbackAction && customCallbackAction()
        }
      })
    } else {
      API.post('/create-manual-contact', {
        ...newManualContact,
        orgID: client._id,
      })
        .then((res) => {
          if (res.status === 200) {
            setToast({ message: 'New Contact Added' })
            setOpenAddNew(false)
            customCallbackAction
              ? customCallbackAction({
                  ...newManualContact,
                  _id: res.data._id,
                })
              : loadEvent()
          }
        })
        .catch((e) => {
          console.error(e)
          setToast({
            message: 'Unable to add contact. Please try again later.',
            severity: 'error',
          })
        })
    }
  }

  const handleDeleteContact = () => {
    API.delete(`/clients/delete-contact/${existingContact?._id}`).then(
      (res) => {
        if (res.status === 200) {
          setToast({ message: 'Contact Deleted' })
          setOpenConfirmDelete(false)
          loadEvent()
        }
      },
    )
  }

  return (
    <Stack gap={3}>
      <Stack gap={1}>
        <Typography variant="title2">
          {existingContact
            ? `Edit ${
                existingContact?.noAccount?.firstName ||
                existingContact?.userAccount?.firstName ||
                ''
              } ${
                existingContact?.noAccount?.lastName ||
                existingContact?.userAccount?.lastName ||
                ''
              } (${client.orgName})`
            : `Add a new Contact to ${client.orgName}`}
        </Typography>
        {loadingPendingAccounts ? (
          <LinearProgress />
        ) : (
          pendingAccounts.length === 0 &&
          !existingContact && (
            <Alert severity="info">
              No pending user accounts found for verification or association.
              You can manually add a new contact to the client or invite a user
              to create an account.
            </Alert>
          )
        )}
      </Stack>
      <Grid container spacing={2} component="form" onSubmit={handleSubmit}>
        {!existingContact && (
          <Grid item xs={12}>
            <Typography variant="title3">Manually add a contact</Typography>
          </Grid>
        )}
        <Grid item xs={12} lg={4}>
          <TextField
            label="First Name"
            fullWidth
            value={newManualContact.firstName ?? ''}
            disabled={hasUserAccount}
            onChange={(e) =>
              setNewManualContact((prev) => ({
                ...prev,
                firstName: e.target.value,
              }))
            }
            required
            autoComplete="none"
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            label="Last Name"
            fullWidth
            disabled={hasUserAccount}
            autoComplete="none"
            value={newManualContact.lastName ?? ''}
            onChange={(e) =>
              setNewManualContact((prev) => ({
                ...prev,
                lastName: e.target.value,
              }))
            }
            required
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            label="Role"
            fullWidth
            autoComplete="none"
            value={newManualContact.role ?? ''}
            onChange={(e) =>
              setNewManualContact((prev) => ({ ...prev, role: e.target.value }))
            }
            required
            disabled={hasUserAccount}
          />
        </Grid>
        <Grid item xs={12} lg={7}>
          <TextField
            label="Email"
            fullWidth
            autoComplete="none"
            value={newManualContact.email ?? ''}
            onChange={(e) =>
              setNewManualContact((prev) => ({
                ...prev,
                email: e.target.value,
              }))
            }
            disabled={hasUserAccount}
          />
        </Grid>
        <Grid item xs={12} lg={5}>
          <TextField
            label="Phone"
            fullWidth
            autoComplete="none"
            value={newManualContact.phone ?? ''}
            onChange={(e) =>
              setNewManualContact((prev) => ({
                ...prev,
                phone: e.target.value,
              }))
            }
            disabled={hasUserAccount}
          />
        </Grid>
        <Grid item xs={12}>
          {!hasUserAccount ? (
            <Stack gap={2}>
              <Button
                color="error"
                variant="outlined"
                startIcon={<Delete />}
                fullWidth
                onClick={() => setOpenConfirmDelete(true)}
              >
                Delete Contact
              </Button>
              <SmallModal
                open={openConfirmDelete}
                onClose={() => setOpenConfirmDelete(false)}
              >
                <Stack alignItems="center" gap={2}>
                  <Typography textAlign="center">
                    Are you sure you want to delete this contact?
                  </Typography>
                  <Button
                    color="error"
                    variant="contained"
                    startIcon={<Delete />}
                    fullWidth
                    onClick={() => handleDeleteContact()}
                  >
                    Yes - Delete
                  </Button>
                  <Button
                    color="contrast"
                    variant="outlined"
                    startIcon={<Delete />}
                    fullWidth
                    onClick={() => setOpenConfirmDelete(false)}
                  >
                    No - Cancel
                  </Button>
                </Stack>
              </SmallModal>
              <Button
                color="success"
                variant="contained"
                type="submit"
                fullWidth
              >
                Submit
              </Button>
            </Stack>
          ) : (
            <Stack gap={1}>
              <Alert severity="info">
                You cannot edit this contact because their details are
                associated with their own User Account / Login
              </Alert>
              {/* TODO: Remove Link with Organisation */}
            </Stack>
          )}
        </Grid>
      </Grid>
    </Stack>
  )
}

export default AddNewContact
